import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Typography, Slider, Row, Col, App } from 'antd';
import { StarOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient, type Brand } from '../services/api';
import { useAppSelector } from '../hooks/redux';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

interface RatingDimension {
  key: string;
  label: string;
  description: string;
}

const ratingDimensions: RatingDimension[] = [
  {
    key: 'environmental',
    label: 'ratebrand.dimension_environmental',
    description: 'ratebrand.desc_environmental',
  },
  {
    key: 'labor_rights',
    label: 'ratebrand.dimension_labor_rights',
    description: 'ratebrand.desc_labor_rights',
  },
  {
    key: 'animal_welfare',
    label: 'ratebrand.dimension_animal_welfare',
    description: 'ratebrand.desc_animal_welfare',
  },
  {
    key: 'transparency',
    label: 'ratebrand.dimension_transparency',
    description: 'ratebrand.desc_transparency',
  },
  {
    key: 'social_responsibility',
    label: 'ratebrand.dimension_social_responsibility',
    description: 'ratebrand.desc_social_responsibility',
  },
  {
    key: 'carbon_footprint',
    label: 'ratebrand.dimension_carbon_footprint',
    description: 'ratebrand.desc_carbon_footprint',
  },
];

interface RatingFormValues {
  environmental: number;
  labor_rights: number;
  animal_welfare: number;
  transparency: number;
  social_responsibility: number;
  carbon_footprint: number;
}

const RateBrandPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated) {
      message.error(t('ratebrand.login_required'));
      navigate('/login');
      return;
    }

    const fetchBrand = async () => {
      try {
        const response = await apiClient.getBrand(parseInt(id!));
        setBrand(response.data);
      } catch (error) {
        message.error(t('ratebrand.load_brand_failed'));
      }
    };

    if (id) {
      fetchBrand();
    }
  }, [id, isAuthenticated, navigate]);

  const onFinish = async (values: RatingFormValues) => {
    setLoading(true);
    try {
      const data = await apiClient.post<{ success: boolean; error?: string }>(
        '/ratings',
        {
          brandId: parseInt(id!),
          ...values,
        }
      );

      if (data.success) {
        message.success(t('ratebrand.submitting_success'));
        navigate(`/brands/${id}`);
      } else {
        message.error(data.error || t('ratebrand.submitting_failed'));
      }
    } catch (error) {
      message.error(t('ratebrand.network_error'));
    } finally {
      setLoading(false);
    }
  };

  if (!brand) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Title level={2}>{t('ratebrand.page_title', { name: brand.name })}</Title>
      <Text type="secondary">{brand.description}</Text>

      <Card style={{ marginTop: 24 }}>
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          initialValues={ratingDimensions.reduce(
            (acc, dim) => ({ ...acc, [dim.key]: 5 }),
            {}
          )}
        >
          {ratingDimensions.map(dimension => (
            <Card key={dimension.key} size="small" style={{ marginBottom: 16 }}>
              <Row>
                <Col span={24}>
                  <Title level={4}>{t(dimension.label)}</Title>
                  <Text type="secondary">{t(dimension.description)}</Text>
                </Col>
              </Row>
              <Form.Item
                name={dimension.key}
                style={{ marginTop: 16, marginBottom: 0 }}
              >
                <Slider
                  min={1}
                  max={10}
                  marks={{
                    1: t('ratebrand.mark_1'),
                    5: t('ratebrand.mark_5'),
                    10: t('ratebrand.mark_10'),
                  }}
                  tipFormatter={value => String(value)}
                />
              </Form.Item>
            </Card>
          ))}

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<StarOutlined />}
              size="large"
              block
            >
              {t('ratebrand.submit_btn')}
            </Button>
            <Button
              style={{ marginTop: 8 }}
              onClick={() => navigate(`/brands/${id}`)}
              block
            >
              {t('ratebrand.cancel_btn')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default RateBrandPage;
